import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Compass, Lightbulb as TipIcon } from "lucide-react";

/** Iter 169 · Frameworks — Lean Canvas / SWOT editors + founder tips library. */

const BLOCK_LABELS = {
  problem: "Problem", customer_segments: "Customer segments",
  unique_value_proposition: "Unique value proposition", solution: "Solution",
  channels: "Channels", revenue_streams: "Revenue streams",
  cost_structure: "Cost structure", key_metrics: "Key metrics",
  unfair_advantage: "Unfair advantage",
  strengths: "Strengths", weaknesses: "Weaknesses",
  opportunities: "Opportunities", threats: "Threats",
};

const SWOT_TONES = {
  strengths: "border-emerald-300 bg-emerald-50/50",
  weaknesses: "border-rose-300 bg-rose-50/50",
  opportunities: "border-sky-300 bg-sky-50/50",
  threats: "border-amber-300 bg-amber-50/50",
};

export default function FrameworksTab({ wsId }) {
  const [library, setLibrary] = useState(null);
  const [ideas, setIdeas] = useState([]);
  const [ventures, setVentures] = useState([]);
  const [entityType, setEntityType] = useState("idea");
  const [entityId, setEntityId] = useState("");
  const [framework, setFramework] = useState("lean_canvas");
  const [doc, setDoc] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/workspaces/${wsId}/bizdev/library`).then((r) => setLibrary(r.data)).catch(() => {});
    api.get(`/workspaces/${wsId}/bizdev/ideas`).then((r) => setIdeas(r.data.rows || [])).catch(() => {});
    api.get(`/workspaces/${wsId}/bizdev/ventures`).then((r) => setVentures(r.data.rows || [])).catch(() => {});
  }, [wsId]);

  const loadDoc = useCallback(async () => {
    if (!entityId) { setDoc(null); return; }
    try {
      const { data } = await api.get(
        `/workspaces/${wsId}/bizdev/frameworks/${entityType}/${entityId}/${framework}`);
      setDoc(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load framework")); }
  }, [wsId, entityType, entityId, framework]);
  useEffect(() => { loadDoc(); }, [loadDoc]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(
        `/workspaces/${wsId}/bizdev/frameworks/${entityType}/${entityId}/${framework}`,
        { data: doc.data });
      setDoc(data);
      toast.success("Framework saved");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };

  const records = entityType === "idea" ? ideas : ventures;
  const blocks = doc ? Object.keys(doc.data) : [];

  return (
    <div className="grid lg:grid-cols-3 gap-4" data-testid="bizdev-frameworks-tab">
      <div className="lg:col-span-2 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={entityType} onValueChange={(v) => { setEntityType(v); setEntityId(""); }}>
            <SelectTrigger className="h-8 w-32 text-xs" data-testid="fw-entity-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="idea">Idea</SelectItem>
              <SelectItem value="venture">Venture</SelectItem>
            </SelectContent>
          </Select>
          <Select value={entityId || "_none"} onValueChange={(v) => setEntityId(v === "_none" ? "" : v)}>
            <SelectTrigger className="h-8 w-64 text-xs" data-testid="fw-entity-select">
              <SelectValue placeholder={entityType === "idea" ? "Pick an idea" : "Pick a venture"} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_none">— pick {entityType === "idea" ? "an idea" : "a venture"} —</SelectItem>
              {records.map((r) => (
                <SelectItem key={r.id} value={r.id}>{r.title || r.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={framework} onValueChange={setFramework}>
            <SelectTrigger className="h-8 w-40 text-xs" data-testid="fw-framework-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="lean_canvas">Lean Canvas</SelectItem>
              <SelectItem value="swot">SWOT</SelectItem>
            </SelectContent>
          </Select>
          {doc && entityId && (
            <Button size="sm" className="h-8 text-xs ml-auto" onClick={save}
                    disabled={saving} data-testid="fw-save-btn">
              {saving ? "Saving…" : "Save framework"}
            </Button>
          )}
        </div>

        {!entityId && (
          <div className="border border-dashed border-border p-12 text-center text-sm text-muted-foreground">
            <Compass className="w-6 h-6 mx-auto mb-2" />
            Pick an idea or venture to fill in a one-page strategy framework.
          </div>
        )}

        {doc && entityId && (
          <div className={`grid gap-2 ${framework === "swot" ? "md:grid-cols-2" : "md:grid-cols-3"}`}
               data-testid="fw-editor-grid">
            {blocks.map((b) => (
              <div key={b}
                   className={`border p-2 ${framework === "swot" ? SWOT_TONES[b] || "border-border" : "border-border bg-card"}`}>
                <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1">
                  {BLOCK_LABELS[b] || b}
                </div>
                <textarea
                  value={doc.data[b] || ""}
                  onChange={(e) => setDoc((d) => ({ ...d, data: { ...d.data, [b]: e.target.value } }))}
                  className="w-full h-24 text-xs p-1.5 border border-border/50 rounded bg-background/70"
                  data-testid={`fw-block-${b}`}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-3">
        <div className="border border-border bg-card p-4">
          <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
            <Compass className="w-3.5 h-3.5" /> Framework guide
          </h3>
          <div className="space-y-2.5">
            {(library?.frameworks || []).map((f) => (
              <div key={f.key} className="text-xs">
                <div className="font-semibold">{f.name}
                  <span className="text-muted-foreground font-normal"> · {f.who}</span>
                </div>
                <div className="text-muted-foreground">{f.use_when}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="border border-border bg-card p-4" data-testid="fw-tips-panel">
          <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
            <TipIcon className="w-3.5 h-3.5" /> Founder productivity tips
          </h3>
          <div className="space-y-2.5">
            {(library?.tips || []).map((t, i) => (
              <div key={i} className="text-xs border-l-2 border-primary/60 pl-2">
                <div className="font-semibold">{t.title}</div>
                <div className="text-muted-foreground">{t.body}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
